import React from 'react';

interface PageSkeletonProps {
  /** Show header skeleton */
  showHeader?: boolean;
  /** Show toolbar/actions skeleton */
  showToolbar?: boolean;
  /** Show tabs skeleton */
  showTabs?: boolean;
  /** Content skeleton type */
  contentType?: 'table' | 'cards' | 'list' | 'form' | 'default';
  /** Custom className */
  className?: string;
}

/**
 * PageSkeleton Component
 * 
 * A layout-aware skeleton for full pages that maintains the page structure
 * while loading. This prevents white screens and provides smooth UX.
 * 
 * Usage:
 * ```tsx
 * <Suspense fallback={<PageSkeleton contentType="table" />}>
 *   <ClientsPage />
 * </Suspense>
 * ```
 */
export const PageSkeleton: React.FC<PageSkeletonProps> = ({
  showHeader = true,
  showToolbar = true,
  showTabs = false,
  contentType = 'default',
  className = '',
}) => {
  return (
    <div className={`animate-pulse space-y-4 ${className}`} dir="rtl">
      {/* Header Section */}
      {showHeader && (
        <div className="space-y-3">
          <div className="h-8 w-64 rounded-lg bg-gray-200" />
          {showToolbar && (
            <div className="flex items-center gap-3">
              <div className="h-10 w-32 rounded-lg bg-gray-200" />
              <div className="h-10 w-32 rounded-lg bg-gray-200" />
              <div className="h-10 w-24 rounded-lg bg-gray-200" />
            </div>
          )}
        </div>
      )}

      {/* Tabs Section */}
      {showTabs && (
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          <div className="h-10 w-24 rounded-lg bg-gray-200" />
          <div className="h-10 w-24 rounded-lg bg-gray-200" />
          <div className="h-10 w-24 rounded-lg bg-gray-200" />
        </div>
      )}

      {/* Content Section */}
      {contentType === 'table' && (
        <div className="space-y-3">
          {/* Table Header */}
          <div className="flex items-center gap-4 py-3 border-b border-gray-200">
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="h-4 w-24 rounded bg-gray-200" />
          </div>
          {/* Table Rows */}
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100">
              <div className="h-4 w-32 rounded bg-gray-100" />
              <div className="h-4 w-32 rounded bg-gray-100" />
              <div className="h-4 w-32 rounded bg-gray-100" />
              <div className="h-4 w-32 rounded bg-gray-100" />
              <div className="h-4 w-24 rounded bg-gray-100" />
            </div>
          ))}
        </div>
      )}

      {contentType === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl bg-white border border-gray-200 p-6 shadow-sm"
            >
              <div className="h-4 w-1/3 rounded-lg bg-gray-200 mb-4" />
              <div className="h-8 w-2/3 rounded-lg bg-gray-200 mb-2" />
              <div className="h-4 w-full rounded-lg bg-gray-200" />
            </div>
          ))}
        </div>
      )}

      {contentType === 'list' && (
        <div className="space-y-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100">
              <div className="h-12 w-12 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 rounded bg-gray-200" />
                <div className="h-3 w-1/2 rounded bg-gray-100" />
              </div>
              <div className="h-8 w-20 rounded-lg bg-gray-200" />
            </div>
          ))}
        </div>
      )}

      {contentType === 'form' && (
        <div className="space-y-6 max-w-2xl">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 rounded bg-gray-200" />
              <div className="h-10 w-full rounded-lg bg-gray-200" />
            </div>
          ))}
          <div className="flex gap-3 pt-4">
            <div className="h-10 w-24 rounded-lg bg-gray-200" />
            <div className="h-10 w-24 rounded-lg bg-gray-200" />
          </div>
        </div>
      )}

      {contentType === 'default' && (
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-gray-200" />
          <div className="h-4 w-5/6 rounded bg-gray-200" />
          <div className="h-4 w-4/6 rounded bg-gray-200" />
          <div className="h-32 w-full rounded-lg bg-gray-200 mt-6" />
        </div>
      )}
    </div>
  );
};

